import React, { memo } from 'react';
import PropTypes from 'prop-types';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import ChangeStartDateModal from './ChangeStartDateModal';
import ChangeTargetDateModal from './ChangeTargetDateModal';
import QuickView from './QuickView/QuickView';
import './CalendarView.scss';
import { CORE_STATUSES, EVENT_DATE_FORMAT } from '../../../constants/constants';
import Moment from 'moment';

const EVENTSOURCES = {
	taskEvents: 1,
	projectEvents: 2,
};

const CALENDAR_VIEWS = {
	month: 'Month',
	week: 'Week',
	day: 'Day',
};

// Memoize the renderDayHeader function
const RenderDayHeader = memo(({ arg, currentView }) => {
	if (arg.view.type === 'dayGridWeek' && currentView === 'dayGridWeek') {
		const dow = Moment.utc(arg.date).format('ddd');
		return <div>{dow}</div>; // Only display the day of the week for week view
	}
	// Return the default header content for other views
	return arg.text;
});

// Define prop types
RenderDayHeader.propTypes = {
	arg: PropTypes.shape({
		view: PropTypes.shape({
			type: PropTypes.string.isRequired,
		}).isRequired,
		date: PropTypes.instanceOf(Date).isRequired,
		text: PropTypes.string.isRequired,
	}).isRequired,
	currentView: PropTypes.string.isRequired,
};

RenderDayHeader.displayName = 'RenderDayHeader';

// Memoize the renderDayCell function
const RenderDayCell = memo(({ arg, currentView }) => {
	if (arg.view.type === 'dayGridWeek' && currentView === 'dayGridWeek') {
		const date = Moment.utc(arg.date).format('D');

		return (
			<div>
				<div style={{ textAlign: 'right', marginBottom: '5px' }}>{date}</div> {/* Display date above events */}
				<div>{arg.dayNumberText}</div> {/* The rest of the content, like events, will appear below */}
			</div>
		);
	}
	// Return the default day cell content for other views
	return arg.dayNumberText;
});

// Define prop types
RenderDayCell.propTypes = {
	arg: PropTypes.shape({
		view: PropTypes.shape({
			type: PropTypes.string.isRequired,
		}).isRequired,
		date: PropTypes.instanceOf(Date).isRequired,
		dayNumberText: PropTypes.string.isRequired,
	}).isRequired,
	currentView: PropTypes.string.isRequired,
};

RenderDayCell.displayName = 'RenderDayCell';

export class CalendarView extends React.Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			calendarRef: React.createRef(),
			currentView: 'dayGridMonth', // set this and immediately change it to month (IE HACK)
			lastHoveredEvent: null,
			lastClickedEvent: null,
			isLoading: false,
			taskEvents: [],
			projectEvents: [],
			showChangeStartDateModal: false,
			showChangeTargetDateModal: false,
			dateChangeDeltaDays: 0, // will go negative or positive
			dateChangeRevertFunc: null, // pointer to the revert function of the resize/drop event that is called if they cancel the confirm modal
			calendarLoaded: false,
		};
	}

	componentDidMount = async () => {
		this.convertThingsToCalendarEvents(this.props.locationTasks);
		document.querySelector('.fc-monthButton-button').classList.add('fc-active');
	};

	componentDidUpdate(prevProps) {
		if (this.props.lastSearchTime && this.props.lastSearchTime !== prevProps.lastSearchTime) {
			this.convertThingsToCalendarEvents(this.props.locationTasks);
		}
		if (!this.state.calendarLoaded) {
			// change the view of the calendar so the events work (IE HACK)
			// None of the calendar events work in IE until the API is invoked
			let calendar = this.state.calendarRef.current;
			if (calendar) {
				let api = calendar.getApi();
				api.changeView('dayGridMonth');
				this.setState({ calendarLoaded: true });
			}
		}
	}

	convertThingsToCalendarEvents = (locationTasks) => {
		// multiple event sources since projects and location tasks could potentially share the same id
		const taskEvents = [];
		const projectEvents = [];

		// converts location tasks to calendar events
		for (let i = 0; i < locationTasks.length; i++) {
			const locationTask = locationTasks[i];

			let eventTask = {
				id: locationTask.id,
				title: locationTask.name,
				allDay: true, // there are no times for any target or start dates so all events are allDay
				locationTask: locationTask, // adding this here will give us access to the obj later in extendedProps
				durationEditable: false, // default - determined later
				startEditable: true, // default - anyone can start a task early
				noStartDate: false,
			};

			if (this.props.settings.allowLocationTaskDateChanges) {
				eventTask.durationEditable = true;
			}

			// if the task has no start date, make the event.start the task.targetDate
			if (!locationTask.startDate) {
				eventTask.noStartDate = true; // remember this for drag and drop
				eventTask.start = Moment.utc(locationTask.targetDate).format(EVENT_DATE_FORMAT);
				eventTask.end = Moment.utc(locationTask.targetDate).format(EVENT_DATE_FORMAT);
			} else {
				eventTask.start = Moment.utc(locationTask.startDate).format(EVENT_DATE_FORMAT);

				if (locationTask.startDate != locationTask.targetDate) {
					// add an extra day so the event goes through the targetDate
					eventTask.end = Moment.utc(locationTask.targetDate).add(1, 'days').format(EVENT_DATE_FORMAT);
				} else {
					eventTask.end = Moment.utc(locationTask.targetDate).format(EVENT_DATE_FORMAT);
				}
			}

			taskEvents.push(eventTask);
		}

		// add events for project locations if they have a zero date
		for (let i = 0; i < this.props.columnFilters.projects.length; i++) {
			const project = this.props.columnFilters.projects[i];
			if (project.zeroDate) {
				let eventZeroDate = {
					id: project.id,
					title: 'ZD: ' + project.name,
					allDay: true,
					project: project,
					start: project.zeroDate,
					end: project.zeroDate,
					durationEditable: false,
					startEditable: false,
				};
				projectEvents.push(eventZeroDate);
			}
		}

		this.setState({ taskEvents: taskEvents, projectEvents: projectEvents }, this.closeQuickView);
	};

	renderEvent = (obj) => {
		if (obj.event.extendedProps.locationTask) {
			const task = obj.event.extendedProps.locationTask;

			// change the border of the event to the color of the status
			if (task.coreStatusId === CORE_STATUSES.Complete) {
				obj.el.classList.add('eh-event-complete');
			} else if (task.coreStatusId === CORE_STATUSES.NotApplicable) {
				obj.el.classList.add('eh-event-notdue');
			} else {
				const targetDate = Moment.utc(task.targetDate);
				if (Moment().isAfter(targetDate.add(1, 'days'))) {
					obj.el.classList.add('eh-event-pastdue');
				} else {
					if (task.coreStatusId === CORE_STATUSES.NotStarted) {
						obj.el.classList.add('eh-event-notdue');
					} else {
						obj.el.classList.add('eh-event-inprogress');
					}
				}
			}

			// this will show our yellow milestone character in the event title
			if (task.isMilestone) {
				obj.el.classList.add('fc-milestone-event');
			}
		} else if (obj.event.extendedProps.project) {
			// maybe do something for these
		}
	};

	renderEventTitle = () => {
		// displays the full non-truncated title of the event in a tooltip on hover
		if (this.state.lastHoveredEvent) {
			if (this.state.lastHoveredEvent.extendedProps.locationTask) {
				return this.state.lastHoveredEvent.extendedProps.locationTask.name;
			} else if (this.state.lastHoveredEvent.extendedProps.project) {
				return this.state.lastHoveredEvent.extendedProps.project.name;
			}
		}
	};

	renderQuickView = () => {
		if (this.state.lastClickedEvent) {
			return (
				<QuickView
					onShowMore={this.openTaskDrawer}
					onStatusChangedComplete={this.props.onStatusChangedComplete}
					statuses={this.props.columnFilters.statuses}
					task={this.state.lastClickedEvent.extendedProps.locationTask}
					isProjectWidgetActive={this.props.settings.isProjectWidgetActive}
				/>
			);
		}
	};

	closeQuickView = () => {
		let box = document.getElementById('event-quick-view-box');
		if (box && box.classList.contains('active')) {
			box.classList.remove('active');
		}
		this.setState({ lastClickedEvent: null });
	};

	openTaskDrawer = () => {
		this.closeQuickView();
		this.props.onSelectTask(this.state.lastClickedEvent.extendedProps.locationTask);
	};

	datesChanged = (info) => {
		this.closeQuickView();
		this.props.onCalendarDateChange(info.view.activeStart, info.view.activeEnd);
	};

	eventClick = (obj) => {
		// remove the tooltip if it is visible
		this.eventMouseLeave();

		// if another event besides a location task was clicked then close any quick views and exit
		if (!obj.event.extendedProps.locationTask) {
			this.closeQuickView();
			return;
		}

		let x = obj.jsEvent.clientX;
		let y = obj.jsEvent.clientY;

		// if the box goes over the width/height of the screen then set the coordinates differently
		if (x + 350 > window.innerWidth) {
			x = window.innerWidth - 350;
		}

		if (y + 500 > window.innerHeight) {
			y = window.innerHeight - 500;
		}

		let box = document.getElementById('event-quick-view-box');
		box.style.top = y + 10 + 'px';
		box.style.left = x + 10 + 'px';

		box.classList.add('active');
		this.setState({ lastClickedEvent: obj.event });
	};

	// #########################
	// The mouse events are for displaying the title of the event when hovering over an event
	eventMouseEnter = (obj) => {
		// only show hover tooltip if no quick views are open
		if (this.state.lastClickedEvent == null) {
			//obj = {event, element, jsEvent, view}
			const x = obj.jsEvent.clientX;
			const y = obj.jsEvent.clientY;

			let box = document.getElementById('event-title-box');
			box.style.top = y + 10 + 'px';
			box.style.left = x + 10 + 'px';

			box.classList.add('active');

			this.setState({ lastHoveredEvent: obj.event });
		}
	};

	eventMouseLeave = () => {
		let box = document.getElementById('event-title-box');
		box.classList.remove('active');
	};

	// #########################
	// The drag and drop events for moving events around on the calendar
	eventDragStart = (obj) => {
		const event = obj.event;
		// also remove the hover effect
		this.setState({ lastHoveredEvent: null, lastClickedEvent: event });
	};

	eventDrop = (obj) => {
		if (obj.event.extendedProps.noStartDate && this.props.settings.allowLocationTaskDateChanges) {
			// changes only the target date
			this.setState({
				showChangeTargetDateModal: true,
				dateChangeDeltaDays: obj.delta.days,
				dateChangeRevertFunc: obj.revert,
			});
		} else {
			// allows for changing both start and target dates, but contains logic to only allow target date change if user has access
			this.setState({
				showChangeStartDateModal: true,
				dateChangeDeltaDays: obj.delta.days,
				dateChangeRevertFunc: obj.revert,
			});
		}
	};

	// #########################
	// The resize events for extending the duration of an event
	eventResizeStart = (obj) => {
		const event = obj.event;
		// also remove the hover effect
		this.setState({ lastHoveredEvent: null, lastClickedEvent: event });
	};

	eventResize = (obj) => {
		this.setState({
			showChangeTargetDateModal: true,
			dateChangeDeltaDays: obj.endDelta.days,
			dateChangeRevertFunc: obj.revert,
		});
	};

	onViewChange = (obj) => {
		const selectedView = this.getSelectedView(obj.target.innerText);

		if (this.state.currentView !== selectedView) {
			let calendarApi = this.state.calendarRef.current.getApi();

			// Update the view only if it's different from the current view
			this.setState({ currentView: selectedView }, () => {
				calendarApi.changeView(selectedView);
			});

			document.querySelectorAll('.fc-button').forEach((el) => {
				el.classList.remove('fc-active');
			});
			obj.target.classList.add('fc-active');
		}
	};

	getSelectedView = (viewText) => {
		switch (viewText) {
			case CALENDAR_VIEWS.month:
				return 'dayGridMonth';
			case CALENDAR_VIEWS.week:
				return 'dayGridWeek';
			case CALENDAR_VIEWS.day:
				return 'dayGridDay';
			default:
				return this.state.currentView;
		}
	};

	createCustomButtons = () => {
		return {
			monthButton: {
				text: CALENDAR_VIEWS.month,
				className: 'fc-active',
				click: this.onViewChange,
			},
			weekButton: {
				text: CALENDAR_VIEWS.week,
				click: this.onViewChange,
			},
			dayButton: {
				text: CALENDAR_VIEWS.day,
				click: this.onViewChange,
			},
		};
	};

	onCloseChangeStartDateModal = () => {
		this.state.dateChangeRevertFunc();
		this.setState({ showChangeStartDateModal: false });
	};

	onCompleteChangeTargetDateModal = () => {
		this.setState({ showChangeTargetDateModal: false }, () => {
			const locationTask = this.state.lastClickedEvent.extendedProps.locationTask;
			const newTargetDate = Moment(locationTask.targetDate).add(this.state.dateChangeDeltaDays, 'days');
			this.props.updateTaskDates(locationTask.id, null, newTargetDate);
		});
	};

	onCloseChangeTargetDateModal = () => {
		this.state.dateChangeRevertFunc();
		this.setState({ showChangeTargetDateModal: false });
	};

	onCompleteChangeStartDateModal = (startDate, targetDate) => {
		this.setState({ showChangeStartDateModal: false }, () => {
			const locationTask = this.state.lastClickedEvent.extendedProps.locationTask;
			this.props.updateTaskDates(locationTask.id, startDate, targetDate);
		});
	};

	renderDayHeader = (arg) => {
		return <RenderDayHeader arg={arg} currentView={this.state.currentView} />;
	};

	renderDayCell = (arg) => {
		return <RenderDayCell arg={arg} currentView={this.state.currentView} />;
	};

	render() {
		// additional styling in the css file
		const calendarHeader = { left: 'prev,next today', center: 'title', right: 'monthButton,weekButton,dayButton' };
		const customButtons = this.createCustomButtons();

		// there are css styles specific to the types of events
		const taskEventSource = {
			id: EVENTSOURCES.taskEvents,
			events: this.state.taskEvents,
			className: 'eh-task-event',
		};

		const projectEventSource = {
			id: EVENTSOURCES.projectEvents,
			events: this.state.projectEvents,
			className: 'eh-project-event',
		};

		const allEvents = [taskEventSource, projectEventSource];

		return (
			<div className='calendar-view'>
				<div id='event-quick-view-box'>{this.renderQuickView()}</div>

				<div id='event-title-box'>{this.renderEventTitle()}</div>

				<LoadingIndicator isLoading={this.state.isLoading || this.props.isFetchingTasks}>
					<FullCalendar
						ref={this.state.calendarRef}
						timeZone='UTC'
						initialView={this.state.currentView}
						editable={true}
						eventStartEditable={true}
						plugins={[dayGridPlugin, interactionPlugin]}
						eventSources={allEvents}
						eventDidMount={this.renderEvent}
						customButtons={customButtons}
						headerToolbar={calendarHeader}
						contentHeight={window.innerHeight - 260}
						eventClick={this.eventClick}
						eventMouseLeave={this.eventMouseLeave}
						eventDragStart={this.eventDragStart}
						eventDrop={this.eventDrop}
						eventResizeStart={this.eventResizeStart}
						eventResize={this.eventResize}
						dateClick={this.closeQuickView}
						datesSet={this.datesChanged}
						buttonText={{
							today: 'Today',
						}}
						dayHeaderContent={this.renderDayHeader} // Custom header content
						dayCellContent={this.renderDayCell} // Custom day cell content
					/>
				</LoadingIndicator>
				{this.state.showChangeStartDateModal && (
					<ChangeStartDateModal
						task={this.state.lastClickedEvent.extendedProps.locationTask}
						onCancel={this.onCloseChangeStartDateModal}
						onComplete={this.onCompleteChangeStartDateModal}
						deltaDays={this.state.dateChangeDeltaDays}
						userCanChangeTargetDate={this.props.settings.allowLocationTaskDateChanges}
					/>
				)}
				{this.state.showChangeTargetDateModal && (
					<ChangeTargetDateModal
						task={this.state.lastClickedEvent.extendedProps.locationTask}
						onCancel={this.onCloseChangeTargetDateModal}
						onComplete={this.onCompleteChangeTargetDateModal}
						deltaDays={this.state.dateChangeDeltaDays}
					/>
				)}
			</div>
		);
	}
}

CalendarView.propTypes = {
	locationTasks: PropTypes.array.isRequired,
	onSelectTask: PropTypes.func.isRequired,
	onStatusChangedComplete: PropTypes.func.isRequired,
	isFetchingTasks: PropTypes.bool.isRequired,
	columnFilters: PropTypes.object.isRequired,
	settings: PropTypes.object.isRequired,
	updateTaskDates: PropTypes.func.isRequired,
	lastSearchTime: PropTypes.instanceOf(Date),
	onCalendarDateChange: PropTypes.func.isRequired,
};

export default CalendarView;
